import React from 'react';
import { observer } from 'mobx-react';
import { observable, computed } from 'mobx';
import ErrorBoundary from 'components/hoc/error_boundary.js';
import styled from '@emotion/styled';
import { flexCenter } from '@seedlang/style_mixins';
import Text from 'components/text';
import VocabProgressBar from 'components/vocab/vocab_progress_bar';
import { LearnedIcon } from '@seedlang/icons';
import Link from 'components/link';
import Checkbox from 'components/checkbox';
import Frequency from 'components/vocab/frequency';
import { AppUI, VocabUI } from '@seedlang/state';
import VocabSourceForUser from 'components/vocab/vocab_source_for_user';
import { Constants , Theme } from '@seedlang/constants';
import ReactTooltip from 'react-tooltip';
import { isBlank, withoutDigits } from '@seedlang/utils';
import { buildSearchHighlightTerms, splitTextBySearchTerms } from 'utils/search_highlight';

const Wrapper = styled.div`
  display: ${props => props.sharedColumns ? 'contents' : 'flex'};
  flex-direction: column;
`;

const Word = styled.div`
  flex: 1;
  padding-left: 3px;
  .text {
    border-bottom: ${props => props.clickable ? '2px solid #b5b5b5' : null};
    display: inline-block;
  }
  .register {
    color: ${Theme.gray};
  }
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${props => props.relatedWord ? '0' : '5px'};
  border-bottom: 1px solid #cccccc;
  padding: ${props => props.relatedWord ? '14px 10px' : '0 0 5px'};
  ${props => props.sharedColumns && `
    display: grid;
    grid-column: 1 / -1;
    grid-template-columns: subgrid;
    > div { min-width: 0; overflow-wrap: anywhere; }
  `}
`;

const Cell = styled.div`
  ${flexCenter()}
  width: ${props => props.width};
  flex: ${props => props.expand ? 1 : null};
  ${props => props.leftAlign && 'justify-content: flex-start; min-width: 0; text-align: left;'}
  padding: 0 5px;
  .selection-button {
    width: 100%;
  }
  .selection-icon-wrapper {
    cursor: pointer;
    width: 26px;
    height: 26px;
    border-radius: 13px;
    background: ${props => props.iconBackground || Theme.blue};
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .selection-icon-wrapper i {
    color: #FFF;
    font-size: 12px;
    margin: 0;
  }
`;

const NumTranslations = styled.div`
  font-size: 12px;
  color: #7d7d7d;
  font-style: italic;
  text-align: left;
  width: 100%;
`;

const MobileSource = styled.div`
  width: 100%;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  .text {
    border-bottom: 0;
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

@observer
class VocabListWord extends React.Component {
  @observable language = 'de';
  @observable sourceEditWord = false;

  onClick = (event) => {
    if (event && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || (event.button !== undefined && event.button !== 0))) {
      return;
    }
    if (this.props.onSelectWord) {
      if (event?.preventDefault) {
        event.preventDefault();
      }
      this.props.onSelectWord(this.props.word);
      return;
    }
    if (this.props.relatedWord) {
      event?.preventDefault?.();
    }
    this.props.setWord(this.props.word);
  }

  @computed get visibleSourcesForUser() {
    if (this.props.sources) return this.props.sources;
    if (!this.props.word || !this.props.user) {
      return [];
    }
    if (this.props.word.visibleVocabSourcesForUser) {
      return this.props.word.visibleVocabSourcesForUser(this.props.user);
    }
    if (this.props.word.vocabSourceForUser) {
      return this.props.word.vocabSourceForUser(this.props.user) || [];
    }
    return [];
  }

  sourceLineKey = (translationSource) => {
    if (!translationSource?.source) {
      return null;
    }
    const languageId = translationSource.source.languageId || 'unknown';
    const sourceText = translationSource.source.textForUser ? translationSource.source.textForUser(this.props.user) : translationSource.source.text;
    const genderText = translationSource.source.displayGender ? this.props.word?.genderAbbreviation || '' : '';
    return `${languageId}:${sourceText || ''}${genderText}`;
  }

  dedupeSourcesForDisplay = (sources) => {
    const seen = new Set();
    return (sources || []).filter(translationSource => {
      const key = this.sourceLineKey(translationSource);
      if (!key || seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }

  isEnglishLanguageId = languageId => {
    return Boolean(languageId && languageId.startsWith('EN'));
  }

  nativeMeaningFallbackSources = () => {
    const shouldShowMeaningSources = this.props.user?.showMeanings && this.props.user?.showVocabMeanings !== false;
    if (!shouldShowMeaningSources) {
      return [];
    }
    const userLanguageId = this.props.user?.languageId;
    const meanings = this.props.word?.meanings || [];
    const nativePerMeaningLimit = meanings.length === 1 ? 2 : 1;
    const sources = [];
    meanings.forEach(meaning => {
      const orderedSources = meaning.translationSourcesForUser
        ? meaning.translationSourcesForUser(this.props.user)
        : (meaning.translationSources || []);
      let selectedCount = 0;
      orderedSources.forEach(translationSource => {
        if (selectedCount >= nativePerMeaningLimit || !translationSource?.source) {
          return;
        }
        if (translationSource.source.languageId === userLanguageId) {
          sources.push(translationSource);
          selectedCount += 1;
        }
      });
    });
    return this.dedupeSourcesForDisplay(sources);
  }

  englishMeaningFallbackSources = () => {
    const shouldShowMeaningSources = this.props.user?.showMeanings && this.props.user?.showVocabMeanings !== false;
    if (!shouldShowMeaningSources) {
      return [];
    }
    const meanings = this.props.word?.meanings || [];
    const sources = [];
    meanings.forEach(meaning => {
      const orderedSources = meaning.translationSourcesForUser
        ? meaning.translationSourcesForUser(this.props.user)
        : (meaning.translationSources || []);
      let selectedCount = 0;
      orderedSources.forEach(translationSource => {
        if (selectedCount >= 1 || !translationSource?.source) {
          return;
        }
        if (this.isEnglishLanguageId(translationSource.source.languageId)) {
          sources.push(translationSource);
          selectedCount += 1;
        }
      });
    });
    return this.dedupeSourcesForDisplay(sources);
  }

  @computed get displayedSourcesForUser() {
    const dedupedSources = this.dedupeSourcesForDisplay(this.visibleSourcesForUser);
    if (!this.props.user?.languageId || this.isEnglishLanguageId(this.props.user.languageId) || this.props.user.onlyShowNativeLanguage) {
      return dedupedSources;
    }
    let mergedSources = dedupedSources;
    const hasNativeSource = mergedSources.some(translationSource => !this.isEnglishLanguageId(translationSource.source?.languageId));
    const hasEnglishSource = mergedSources.some(translationSource => this.isEnglishLanguageId(translationSource.source?.languageId));

    if (!hasNativeSource) {
      const nativeFallbackSources = this.nativeMeaningFallbackSources();
      if (nativeFallbackSources.length) {
        if (this.props.user.showNativeLanguageFirst === false) {
          mergedSources = this.dedupeSourcesForDisplay(mergedSources.concat(nativeFallbackSources));
        } else {
          mergedSources = this.dedupeSourcesForDisplay(nativeFallbackSources.concat(mergedSources));
        }
      }
    }

    if (!hasEnglishSource) {
      const englishFallbackSources = this.englishMeaningFallbackSources();
      if (englishFallbackSources.length) {
        if (this.props.user.showNativeLanguageFirst === false) {
          mergedSources = this.dedupeSourcesForDisplay(englishFallbackSources.concat(mergedSources));
        } else {
          mergedSources = this.dedupeSourcesForDisplay(mergedSources.concat(englishFallbackSources));
        }
      }
    }
    return mergedSources;
  }

  mobileNativeMeaningFallbackSources = () => {
    const shouldShowMeaningSources = this.props.user?.showMeanings && this.props.user?.showVocabMeanings !== false;
    if (!shouldShowMeaningSources) {
      return [];
    }
    const userLanguageId = withoutDigits(this.props.user?.languageId);
    const meanings = this.props.word?.meanings || [];
    const nativePerMeaningLimit = meanings.length === 1 ? 2 : 1;
    const sources = [];
    meanings.forEach(meaning => {
      const orderedSources = meaning.translationSourcesForUser
        ? meaning.translationSourcesForUser(this.props.user)
        : (meaning.translationSources || []);
      let selectedCount = 0;
      orderedSources.forEach(translationSource => {
        if (selectedCount >= nativePerMeaningLimit || !translationSource?.source) {
          return;
        }
        if (withoutDigits(translationSource.source.languageId) === userLanguageId) {
          sources.push(translationSource);
          selectedCount += 1;
        }
      });
    });
    return this.dedupeSourcesForDisplay(sources);
  }

  @computed get mobileDisplayedSourcesForUser() {
    const userLanguageId = withoutDigits(this.props.user?.languageId);
    const nativeSources = this.dedupeSourcesForDisplay(this.visibleSourcesForUser).filter(translationSource => {
      return withoutDigits(translationSource.source?.languageId) === userLanguageId;
    });
    if (nativeSources.length > 0) {
      return nativeSources;
    }
    return this.mobileNativeMeaningFallbackSources();
  }

  sourceTextForDisplay = translationSource => {
    if (!translationSource?.source) {
      return null;
    }
    const sourceText = translationSource.source.textForUser ? translationSource.source.textForUser(this.props.user) : translationSource.source.text;
    if (!sourceText) {
      return null;
    }
    if (translationSource.source.displayGender && this.props.word?.genderAbbreviation) {
      return `${sourceText}${this.props.word.genderAbbreviation}`;
    }
    return sourceText;
  }

  @computed get mobileSourceList() {
    return this.mobileDisplayedSourcesForUser
      .map(translationSource => this.sourceTextForDisplay(translationSource))
      .filter(sourceText => Boolean(sourceText))
      .slice(0, 3)
      .join(', ');
  }

  @computed get selectedCardTypeIsCompatible() {
    if (this.props.selectedCardTypeId === undefined || isBlank(VocabUI.selectedWords)) {
      return true;
    }

    switch (Constants.REVIEW_CARD_TYPES.find(item => item.id === this.props.selectedCardTypeId).slug) {
      case 'conjugation':
        return this.props.word.isVerb && !this.props.word.hideFromConjugationTrainer;
      case 'gender':
        return this.props.word.isNoun && !this.props.word.hideFromGenderTrainer;
      case 'plural':
        return this.props.word.isNoun && !this.props.word.hideFromPluralTrainer && !this.props.word.noPlural && this.props.word.languageId === 'DE';
      default:
        return true;
    }
  }

  @computed get searchHighlightTerms() {
    return buildSearchHighlightTerms(this.props.searchQuery, this.props.searchHighlights, [this.props.childTargetExactMatchText]);
  }

  renderHighlightedText = (text) => {
    const segments = splitTextBySearchTerms(text || '', this.searchHighlightTerms);
    return segments.map((segment, index) => {
      if (!segment.highlighted) {
        return <React.Fragment key={`${segment.text}-${index}`}>{segment.text}</React.Fragment>;
      }

      return <strong key={`${segment.text}-${index}`}>{segment.text}</strong>;
    });
  }

  render() {
    const isSelectionMode = Boolean(this.props.onSelectWord);

    return (
      <Wrapper sharedColumns={this.props.relatedWord && this.props.showSource}>
        <Row relatedWord={this.props.relatedWord} sharedColumns={this.props.relatedWord && this.props.showSource}>
          {
            !this.props.relatedWord && !this.props.grammarSection &&
              <Cell
                width='40px'
                data-tip
                data-for={`incompatible-${this.props.word.id}`}
                iconBackground={this.props.word.reviewing ? Theme.green : Theme.blue}
              >
                {
                  isSelectionMode &&
                  <div
                    className='selection-icon-wrapper'
                    onClick={this.onClick}
                  >
                    <i className={`fa ${this.props.word.reviewing ? 'fa-check' : 'fa-plus'}`} />
                  </div>
                }
                {
                  !isSelectionMode &&
                  <>
                    <Checkbox
                      onClick={() => (this.selectedCardTypeIsCompatible || this.props.selectedWordIds.includes(this.props.word.id)) ? this.props.onToggleAddWordToDeck(this.props.word, {}) : null}
                      value={this.props.selectedWordIds.includes(this.props.word.id)}
                      showLock={!this.props.allowSelection || !this.selectedCardTypeIsCompatible}
                    />
                    {
                      !this.props.allowSelection &&
                        <ReactTooltip
                          place='right'
                          type='dark'
                          effect='solid'
                          id={`incompatible-${this.props.word.id}`}
                          class='custom-tooltip'
                        >
                          <div>Become a Pro Member to create</div>
                          <div>multiple reviews at a time.</div>
                        </ReactTooltip>
                    }
                    {
                      this.props.allowSelection && !this.selectedCardTypeIsCompatible &&
                        <ReactTooltip
                          place='right'
                          type='dark'
                          effect='solid'
                          id={`incompatible-${this.props.word.id}`}
                          class='custom-tooltip'
                        >
                          <div>This word is not compatible</div>
                          <div>{`with ${Constants.REVIEW_CARD_TYPES.find(item => item.id === this.props.selectedCardTypeId).name} cards.`}</div>
                        </ReactTooltip>
                    }
                  </>
                }
              </Cell>
          }
          {
            !this.props.relatedWord && !this.props.grammarSection && !this.props.isMobile && AppUI.layout.leftColumnWidth > 700 &&
              <Cell
                width='60px'
              >
                <Text
                  fontSize='13px'
                >
                  {!this.props.word.frequencyRanking && '-'}
                  {
                    this.props.word.frequencyRanking &&
                      <Frequency
                        word={this.props.word}
                      />
                  }
                </Text>
              </Cell>
          }
          <Cell
            expand={!this.props.showSource}
            leftAlign={this.props.relatedWord}
            width={this.props.showSource && !this.props.relatedWord ? '200px' : null}
          >
            <Word
              clickable={this.props.word.publicInVocab}
            >
              {
                this.props.word && (this.props.word.publicInVocab || this.props.relatedWord) &&
                  <Link
                    onClick={this.onClick}
                    state={{dictionaryNavigation: this.props.dictionaryNavigation}}
                    to={{
                      name: 'vocab.word',
                      params: {wordId: this.props.word.id},
                      query: {from: 'vocab-list'},
                    }}
                  >
                    <Text
                      fontSize={this.props.isMobile ? '14px' : '16px'}
                    >
                      {this.language === 'de' ? this.renderHighlightedText(this.props.word.targetTextWithDefiniteArticle) : this.renderHighlightedText(this.props.word.vocabSourceList)}
                    </Text>
                  </Link>
              }
              {
                this.props.word && !this.props.word.publicInVocab && !this.props.relatedWord &&
                  <Text
                    fontSize={this.props.isMobile ? '14px' : '16px'}
                  >
                    {this.language === 'de' ? this.renderHighlightedText(this.props.word.targetTextWithDefiniteArticle) : this.renderHighlightedText(this.props.word.vocabSourceList)}
                  </Text>
              }
              {
                this.props.isMobile && this.mobileSourceList &&
                  <MobileSource>
                    <Text
                      fontSize='12px'
                      lineHeight='16px'
                    >
                      {this.renderHighlightedText(this.mobileSourceList)}
                    </Text>
                  </MobileSource>
              }
            </Word>
          </Cell>
          {
            this.props.showSource &&
              <Cell
                expand
                leftAlign={this.props.relatedWord}
              >
                {this.props.relatedWord && (
                  <Text fontSize='14px'>
                    {this.renderHighlightedText(this.mobileSourceList)}
                  </Text>
                )}
                {
                  !this.props.relatedWord && !this.props.expandTranslation && this.props.word && this.props.word.vocabSourceForUser &&
                    <NumTranslations>
                      {this.displayedSourcesForUser.length} translations
                    </NumTranslations>
                }
                {
                  !this.props.relatedWord && this.props.expandTranslation && this.props.word && this.props.word.vocabSourceForUser &&
                    <VocabSourceForUser
                      onClick={() => this.sourceEditWord = true}
                      sources={this.displayedSourcesForUser}
                      genderAbbreviation={this.props.word.genderAbbreviation}
                      user={this.props.user}
                      word={this.props.word}
                      highlightTerms={this.searchHighlightTerms}
                    />
                }
              </Cell>
          }
          {this.props.relatedWord && (
            <Cell width='50px'>
              {this.props.word.frequencyRanking ? (
                <Frequency word={this.props.word} tooltipPlace={this.props.inDeck ? 'left' : 'top'} />
              ) : <Text fontSize='13px'>–</Text>}
            </Cell>
          )}
          {!this.props.relatedWord && <Cell
            width='70px'
            center
          >
            <VocabProgressBar
              word={this.props.word}
            />
          </Cell>}
          {!this.props.relatedWord && <Cell
            width='65px'
            center
          >
            <LearnedIcon
              width='28px'
              selected={this.props.word.learned}
              onClick={() => this.props.onToggleLearned(this.props.word)}
            />
          </Cell>}
        </Row>
      </Wrapper>
    );
  }
}

export default ErrorBoundary(VocabListWord);
